import {
  ArrowClockwiseIcon,
  CircleNotchIcon,
  PaperclipIcon,
  WarningCircleIcon,
  X,
} from '@phosphor-icons/react';
import { T, Var, useGT } from 'gt-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';

export interface ChatInputAttachment {
  documentId: string;
  fileName: string;
  uploading?: boolean;
  progressPercent?: number;
  errorMessage?: string;
}

interface ChatInputAttachmentsProps {
  attachments: ChatInputAttachment[];
  onRemove?: (documentId: string) => void;
  onRetry?: (documentId: string) => void;
}

function AttachmentStatusIcon({
  attachment,
}: {
  attachment: ChatInputAttachment;
}) {
  if (attachment.errorMessage)
    return <WarningCircleIcon className="size-3 shrink-0 text-destructive" />;
  if (attachment.uploading)
    return (
      <CircleNotchIcon className="size-3 shrink-0 animate-spin text-muted-foreground" />
    );
  return <PaperclipIcon className="size-3 shrink-0 text-muted-foreground" />;
}

export function ChatInputAttachments({
  attachments,
  onRemove,
  onRetry,
}: ChatInputAttachmentsProps) {
  const gt = useGT();
  return (
    <div className="flex flex-wrap gap-1.5 px-3 pt-2">
      {attachments.map((attachment) => (
        <div
          key={attachment.documentId}
          className="flex max-w-[240px] flex-col gap-1 rounded-md border bg-muted px-2 py-1 text-xs"
        >
          <div className="flex items-center gap-1">
            <AttachmentStatusIcon attachment={attachment} />
            <span className="min-w-0 flex-1 truncate">
              {attachment.fileName}
            </span>
            {attachment.errorMessage && onRetry && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-5 shrink-0"
                aria-label={gt('Retry upload', {
                  context: 'retry a failed chat attachment upload button',
                })}
                onClick={() => onRetry(attachment.documentId)}
              >
                <ArrowClockwiseIcon />
              </Button>
            )}
            {onRemove && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-5 shrink-0"
                aria-label={gt('Remove attachment', {
                  context: 'remove chat attachment button',
                })}
                onClick={() => onRemove(attachment.documentId)}
              >
                <X />
              </Button>
            )}
          </div>
          {attachment.uploading && attachment.progressPercent !== undefined && (
            <div className="flex items-center gap-2">
              <Progress
                value={attachment.progressPercent}
                className="h-1 min-w-16 flex-1"
              />
              <span className="tabular-nums text-muted-foreground">
                <T context="chat attachment upload percentage">
                  <Var>{Math.round(attachment.progressPercent)}</Var>%
                </T>
              </span>
            </div>
          )}
          {attachment.errorMessage && (
            <span className="truncate text-destructive">
              {attachment.errorMessage}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}
